import { getEntityType, getIDType, QueryEntity } from '@datorama/akita';
import { DateTimeService } from '@y42/date-time';
import { TriggeredByType } from '@y42/json-schema';
import { OrgUserQuery } from '@y42/org-user/state';
import {
  combineLatest,
  defer,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { JobDateRange, JobState, JobTriggeredBy, JobUIModel } from './job.model';
import { JobStore } from './job.store';

export abstract class JobQuery<
  State extends JobState = JobState,
  UIModel extends JobUIModel = JobUIModel,
> extends QueryEntity<State> {
  constructor(
    protected readonly store: JobStore<State>,
    protected readonly dateTimeService: DateTimeService,
    protected readonly orgUserQuery: OrgUserQuery,
    public readonly toUIModel: (storeModel: getEntityType<State>) => UIModel,
  ) {
    super(store);
  }

  protected abstract readonly allJobs$: Observable<UIModel[]>;

  readonly jobs$ = defer(() =>
    combineLatest([this.allJobs$, this.dateRange$]).pipe(
      map(([jobs, { from, to }]) => jobs.filter((job) => this.dateTimeService.isBetween(job.created_at, from, to))),
    ),
  );

  readonly loading$ = this.selectLoading().pipe(
    distinctUntilChanged(),
    switchMap((loading) =>
      loading
        ? of(true)
        : timer(500).pipe(
            startWith(true),
            map(() => false),
          ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly maxDateRange = this.store.maxDateRange;
  readonly selectedDateRangeForm = this.store.selectedDateRangeForm;
  readonly dateRange$: Observable<JobDateRange> = this.store.selectedDateRangeForm.valueChanges.pipe(
    startWith(this.store.selectedDateRangeForm.value),
    map(() => this.store.selectedDateRangeForm.value),
    filter((range): range is JobDateRange => !!range.from && !!range.to),
    map(({ from, to }) => {
      return {
        from: this.dateTimeService.startOfDay(from),
        to: this.dateTimeService.endOfDay(to),
      };
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly lastFetched$: Observable<Date> = this.select((st) => st.ui.lastFetched).pipe(filter(Boolean));
  readonly sortFilter$ = this.select((st) => st.ui.sortFilter);

  getUIEntity(id: getIDType<State>): UIModel | undefined {
    const entity = this.getEntity(id);
    return entity ? this.toUIModel(entity) : undefined;
  }

  selectUIEntity(id: getIDType<State>): Observable<UIModel | undefined> {
    return this.selectEntity(id).pipe(map((entity) => (entity ? this.toUIModel(entity) : undefined)));
  }

  protected getTriggeredBy(storeModel: getEntityType<State>): JobTriggeredBy {
    const triggeredByType = storeModel.request?.context?.triggered_by_type;
    const triggeredById = storeModel.request?.context?.triggered_by_id;
    switch (triggeredByType) {
      case TriggeredByType.User:
        return {
          type: triggeredByType,
          data: this.orgUserQuery.getUIEntity(triggeredById),
        };
      case TriggeredByType.Orchestration:
        return {
          type: triggeredByType,
          data: triggeredById,
        };
      default:
        return {
          type: TriggeredByType.User,
          data: undefined,
        };
    }
  }
}
